<template>
    <section class="enterprise-contact-page">
        <div class="enterprise-contact-hero">
            <div class="enterprise-contact-copy">
                <p class="enterprise-contact-badge">Enterprise Plan</p>
                <h1>Thanks for choosing {{ planName }}</h1>
                <p class="enterprise-contact-description">
                    This plan is available for enterprise customers.
                </p>
                <p class="enterprise-contact-description">
                    To activate your account and get started, please contact
                    your administrator or our enterprise team.
                </p>
            </div>

            <div class="enterprise-contact-illustration" aria-hidden="true">
                <div class="enterprise-scene">
                    <div class="enterprise-scene__halo"></div>
                    <div class="enterprise-scene__screen">
                        <div class="enterprise-scene__screen-top"></div>
                        <div class="enterprise-scene__screen-body"></div>
                    </div>
                    <div class="enterprise-scene__desk"></div>
                    <div class="enterprise-scene__person enterprise-scene__person--left"></div>
                    <div class="enterprise-scene__person enterprise-scene__person--right"></div>
                    <div class="enterprise-scene__mail"></div>
                </div>
            </div>
        </div>

        <div class="enterprise-contact-main">
            <v-card
                class="enterprise-card enterprise-card--contact"
                variant="outlined"
            >
                <div class="enterprise-card__header">
                    <div class="enterprise-card__icon">
                        <v-icon icon="mdi-account-group-outline" />
                    </div>
                    <div>
                        <h2>Contact Your Administrator or Sales</h2>
                        <p>
                            For enterprise plans, activation and billing are
                            handled by your administrator or our enterprise
                            team. No payment is collected at this time.
                        </p>
                    </div>
                </div>

                <div class="enterprise-card-grid">
                    <article class="enterprise-panel">
                        <div class="enterprise-panel__icon">
                            <v-icon icon="mdi-office-building-outline" />
                        </div>
                        <h3>Contact Your Administrator</h3>
                        <p>
                            If you already work with an administrator at your
                            organization, they can help activate your plan and
                            set up your workspace.
                        </p>
                        <v-btn
                            block
                            color="primary"
                            variant="outlined"
                            class="enterprise-panel__cta"
                            :href="adminContactHref"
                        >
                            Contact Admin
                        </v-btn>
                    </article>

                    <article class="enterprise-panel">
                        <div class="enterprise-panel__icon">
                            <v-icon icon="mdi-email-outline" />
                        </div>
                        <h3>Contact Sales</h3>
                        <p>
                            New to SmartData or need help? Our enterprise
                            specialists are here to help you find the right
                            solution.
                        </p>
                        <v-btn
                            block
                            color="primary"
                            variant="outlined"
                            class="enterprise-panel__cta"
                            :href="salesContactHref"
                        >
                            Contact Sales
                        </v-btn>
                    </article>
                </div>

                <div class="enterprise-inline-note">
                    <v-icon icon="mdi-information-outline" />
                    <span>
                        You can always return to this page while we set things
                        up.
                    </span>
                </div>
            </v-card>

            <v-card
                class="enterprise-card enterprise-card--summary"
                variant="outlined"
            >
                <div class="enterprise-card__header">
                    <div class="enterprise-card__icon">
                        <v-icon icon="mdi-clipboard-text-outline" />
                    </div>
                    <div>
                        <h2>Your Selected Plan</h2>
                    </div>
                </div>

                <dl class="enterprise-summary-list">
                    <div>
                        <dt>Plan</dt>
                        <dd>{{ planName }}</dd>
                    </div>
                    <div>
                        <dt>Hosted Datasets</dt>
                        <dd>{{ hostedDatasetSummary }}</dd>
                    </div>
                    <div>
                        <dt>Storage Capacity</dt>
                        <dd>{{ storageSummary }}</dd>
                    </div>
                    <div>
                        <dt>Team Seats</dt>
                        <dd>{{ teamSeatsSummary }}</dd>
                    </div>
                    <div>
                        <dt>Max Teams</dt>
                        <dd>{{ maxTeamsSummary }}</dd>
                    </div>
                    <div>
                        <dt>Billing Period</dt>
                        <dd>{{ billingPeriodDisplay }}</dd>
                    </div>
                    <div>
                        <dt>Price</dt>
                        <dd>{{ formattedPrice }}</dd>
                    </div>
                </dl>

                <div class="enterprise-invoice-note">
                    <div class="enterprise-invoice-note__icon">
                        <v-icon icon="mdi-file-document-outline" />
                    </div>
                    <div>
                        <strong>
                            Enterprise plans are custom-quoted and billed via
                            invoice.
                        </strong>
                        <p>
                            Our team will be in touch to complete the setup.
                        </p>
                    </div>
                </div>
            </v-card>
        </div>

        <div class="enterprise-contact-secondary">
            <v-card class="enterprise-card enterprise-card--next" variant="outlined">
                <h2>What happens next?</h2>

                <div class="enterprise-steps">
                    <article
                        v-for="step in nextSteps"
                        :key="step.number"
                        class="enterprise-step"
                    >
                        <div class="enterprise-step__number">
                            {{ step.number }}
                        </div>
                        <div class="enterprise-step__icon">
                            <v-icon :icon="step.icon" />
                        </div>
                        <h3>{{ step.title }}</h3>
                        <p>{{ step.copy }}</p>
                    </article>
                </div>
            </v-card>

            <v-card class="enterprise-card enterprise-card--assist" variant="outlined">
                <div class="enterprise-card__header">
                    <div class="enterprise-card__icon">
                        <v-icon icon="mdi-headset" />
                    </div>
                    <div>
                        <h2>Need immediate assistance?</h2>
                        <p>Our enterprise team is here to help.</p>
                    </div>
                </div>

                <v-btn
                    block
                    color="success"
                    variant="outlined"
                    class="enterprise-assist__cta"
                    :href="salesContactHref"
                >
                    Contact Enterprise Team
                </v-btn>

                <p class="enterprise-assist__email">
                    or email us at
                    <a :href="salesContactHref">{{ salesEmail }}</a>
                </p>
            </v-card>
        </div>

        <v-card class="enterprise-card enterprise-card--security" variant="outlined">
            <div class="enterprise-security__intro">
                <div class="enterprise-card__icon">
                    <v-icon icon="mdi-shield-check-outline" />
                </div>
                <div>
                    <h2>Your data is safe with SmartData</h2>
                    <p>
                        We take security and privacy seriously. All enterprise
                        plans include industry-leading security and compliance.
                    </p>
                </div>
            </div>

            <div class="enterprise-security__items">
                <div v-for="item in securityItems" :key="item.label">
                    <v-icon :icon="item.icon" />
                    <span>{{ item.label }}</span>
                </div>
            </div>
        </v-card>
    </section>
</template>

<script>
import {
    getPlanBillingInterval
} from 'components/BillingPlans/PlanOptions/signupPlanDeepLink';
import {
    formatByoHostedDatasetLimit,
    formatByoStorageLimit
} from 'components/BillingPlans/PlanOptions/byoPlanDisplay';

export default {
    name: 'EnterprisePlanContactFlow',
    props: {
        plan: {
            type: Object,
            default: function() {
                return null;
            }
        }
    },
    computed: {
        planName() {
            return this.plan?.name || 'Selected enterprise plan';
        },
        billingPeriodDisplay() {
            const billingInterval = getPlanBillingInterval(this.plan);
            return billingInterval.charAt(0).toUpperCase() + billingInterval.slice(1);
        },
        formattedPrice() {
            const cost = Number(this.plan?.cost);
            const currency = String(this.plan?.currency || 'AUD').trim();

            if (!Number.isFinite(cost)) return 'Custom quote';
            if (cost < 0) return 'Custom quote';
            if (cost === 0) return `${currency} 0`;

            return `${currency} ${cost.toLocaleString()}`;
        },
        hostedDatasetSummary() {
            return formatByoHostedDatasetLimit(this.plan);
        },
        storageSummary() {
            return formatByoStorageLimit(this.plan);
        },
        teamSeatsSummary() {
            return this.formatCount(
                this.plan?.byo_hosting?.limits?.included_team_seats,
                'seat'
            );
        },
        maxTeamsSummary() {
            return this.formatCount(this.plan?.limit?.team?.max, 'team');
        },
        salesEmail() {
            return 'enterprise@smartdata.com';
        },
        salesContactHref() {
            return `mailto:${this.salesEmail}?subject=${encodeURIComponent(
                `Enterprise plan enquiry: ${this.planName}`
            )}`;
        },
        adminContactHref() {
            return this.salesContactHref;
        },
        nextSteps() {
            return [
                {
                    number: 1,
                    icon: 'mdi-email-outline',
                    title: "We've received your request",
                    copy: `Our team has been notified of your interest in ${this.planName}.`
                },
                {
                    number: 2,
                    icon: 'mdi-account-group-outline',
                    title: 'Our team will reach out',
                    copy: 'An enterprise specialist will contact you to understand your requirements.'
                },
                {
                    number: 3,
                    icon: 'mdi-clipboard-check-outline',
                    title: 'Plan and workspace setup',
                    copy: 'We will set up your plan, team, and workspace for your organization.'
                },
                {
                    number: 4,
                    icon: 'mdi-rocket-launch-outline',
                    title: "You're ready to go",
                    copy: 'Once activated, you will be able to start uploading your datasets.'
                }
            ];
        },
        securityItems() {
            return [
                {
                    icon: 'mdi-shield-check-outline',
                    label: 'SOC 2 Compliant'
                },
                {
                    icon: 'mdi-lock-outline',
                    label: 'GDPR Ready'
                },
                {
                    icon: 'mdi-cloud-outline',
                    label: 'Secure Infrastructure'
                },
                {
                    icon: 'mdi-office-building-outline',
                    label: 'Enterprise Grade Security'
                }
            ];
        }
    },
    methods: {
        formatCount(value, label) {
            if (value === -1) return `Unlimited ${label}s`;
            if (value === null || value === undefined || value === '') {
                return 'Not configured';
            }

            const count = Number(value);
            if (!Number.isFinite(count)) return 'Not configured';

            return `${count.toLocaleString()} ${label}${count === 1 ? '' : 's'}`;
        }
    }
};
</script>

<style scoped>
.enterprise-contact-page {
    width: min(1380px, 100%);
    margin: 0 auto;
    color: var(--sd-text);
    --enterprise-surface: var(--sd-surface);
    --enterprise-surface-soft: var(--sd-surface-soft);
    --enterprise-surface-panel: var(--sd-surface-panel);
    --enterprise-border: var(--sd-border);
    --enterprise-border-soft: var(--sd-border-subtle);
    --enterprise-text: var(--sd-text);
    --enterprise-text-secondary: var(--sd-text-secondary);
    --enterprise-text-muted: var(--sd-text-muted);
    --enterprise-primary: var(--sd-primary);
    --enterprise-primary-soft: var(--sd-primary-surface-alpha);
    --enterprise-primary-soft-alt: var(--sd-primary-surface-soft-alpha);
    --enterprise-primary-border: var(--sd-primary-border-muted);
    --enterprise-success-soft: var(--sd-success-surface-alpha);
    --enterprise-success-border: var(--sd-success-border-alpha);
    --enterprise-warning-soft: rgba(245, 158, 11, 0.12);
    --enterprise-warning-border: rgba(245, 158, 11, 0.28);
    --enterprise-shadow: var(--sd-shadow-soft);
}

.theme--dark .enterprise-contact-page,
.v-theme--dark .enterprise-contact-page,
body.theme--dark .enterprise-contact-page {
    --enterprise-warning-soft: rgba(245, 158, 11, 0.18);
    --enterprise-warning-border: rgba(245, 158, 11, 0.34);
}

.enterprise-contact-hero,
.enterprise-contact-main,
.enterprise-contact-secondary {
    display: grid;
    gap: 24px;
    margin-bottom: 24px;
}

.enterprise-contact-hero,
.enterprise-contact-secondary {
    grid-template-columns: minmax(0, 1fr) 360px;
}

.enterprise-contact-main {
    grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.92fr);
    align-items: start;
}

.enterprise-contact-copy {
    padding: 20px 6px 0 2px;
}

.enterprise-contact-badge {
    display: inline-flex;
    margin: 0 0 18px;
    padding: 8px 12px;
    border-radius: 999px;
    background: var(--enterprise-primary-soft);
    color: var(--enterprise-primary);
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.enterprise-contact-copy h1 {
    margin: 0 0 16px;
    color: var(--enterprise-text);
    font-size: clamp(2.3rem, 4vw, 3.6rem);
    line-height: 1.04;
}

.enterprise-contact-description {
    max-width: 760px;
    margin: 0 0 12px;
    color: var(--enterprise-text-secondary);
    font-size: 1.08rem;
}

.enterprise-contact-illustration {
    position: relative;
    min-height: 260px;
    border-radius: 28px;
    background:
        radial-gradient(circle at 22% 28%, rgba(255, 255, 255, 0.9), transparent 34%),
        linear-gradient(145deg, var(--enterprise-primary-soft-alt), rgba(255, 255, 255, 0));
    overflow: hidden;
}

.enterprise-scene {
    position: absolute;
    inset: 0;
}

.enterprise-scene__halo {
    position: absolute;
    inset: 26px 40px auto auto;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(60, 100, 244, 0.14), transparent 68%);
}

.enterprise-scene__screen {
    position: absolute;
    top: 60px;
    left: 104px;
    width: 180px;
    height: 110px;
    border: 3px solid rgba(60, 100, 244, 0.32);
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 18px 44px rgba(60, 100, 244, 0.12);
}

.enterprise-scene__screen-top {
    position: absolute;
    top: 18px;
    left: 24px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--enterprise-primary-soft);
}

.enterprise-scene__screen-body {
    position: absolute;
    top: 24px;
    right: 18px;
    width: 64px;
    height: 44px;
    border-radius: 10px;
    background:
        linear-gradient(
            180deg,
            rgba(60, 100, 244, 0.22),
            rgba(60, 100, 244, 0.08)
        );
}

.enterprise-scene__desk {
    position: absolute;
    right: 64px;
    bottom: 42px;
    width: 210px;
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(60, 100, 244, 0.3), rgba(60, 100, 244, 0.12));
}

.enterprise-scene__person {
    position: absolute;
    bottom: 54px;
    width: 68px;
    height: 116px;
    border-radius: 28px 28px 18px 18px;
    background: linear-gradient(180deg, rgba(60, 100, 244, 0.96), rgba(60, 100, 244, 0.62));
}

.enterprise-scene__person::before {
    content: '';
    position: absolute;
    top: -30px;
    left: 18px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #ffd4b3;
}

.enterprise-scene__person--left {
    left: 58px;
    height: 86px;
    width: 78px;
}

.enterprise-scene__person--right {
    right: 42px;
}

.enterprise-scene__mail {
    position: absolute;
    top: 28px;
    right: 128px;
    width: 68px;
    height: 46px;
    border: 3px solid rgba(60, 100, 244, 0.76);
    border-radius: 12px;
}

.enterprise-scene__mail::before,
.enterprise-scene__mail::after {
    content: '';
    position: absolute;
    top: 10px;
    width: 28px;
    height: 3px;
    background: rgba(60, 100, 244, 0.76);
}

.enterprise-scene__mail::before {
    left: 2px;
    transform: rotate(35deg);
    transform-origin: left center;
}

.enterprise-scene__mail::after {
    right: 2px;
    transform: rotate(-35deg);
    transform-origin: right center;
}

.enterprise-card {
    border-color: var(--enterprise-border) !important;
    border-radius: 22px !important;
    background: var(--enterprise-surface) !important;
    color: var(--enterprise-text) !important;
    box-shadow: var(--enterprise-shadow) !important;
}

.enterprise-card--contact,
.enterprise-card--summary,
.enterprise-card--next,
.enterprise-card--assist,
.enterprise-card--security {
    padding: 26px;
}

.enterprise-card__header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 20px;
}

.enterprise-card__icon,
.enterprise-panel__icon,
.enterprise-step__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--enterprise-primary-soft);
    color: var(--enterprise-primary);
}

.enterprise-card__header h2,
.enterprise-card--next h2 {
    margin: 0 0 6px;
    color: var(--enterprise-text);
    font-size: 1.65rem;
}

.enterprise-card__header p,
.enterprise-card--security p,
.enterprise-card--assist p {
    margin: 0;
    color: var(--enterprise-text-secondary);
}

.enterprise-card-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
}

.enterprise-panel {
    padding: 22px;
    border: 1px solid var(--enterprise-border);
    border-radius: 18px;
    background: var(--enterprise-surface-panel);
}

.enterprise-panel h3,
.enterprise-step h3 {
    margin: 16px 0 10px;
    color: var(--enterprise-text);
    font-size: 1.2rem;
}

.enterprise-panel p,
.enterprise-step p {
    margin: 0;
    color: var(--enterprise-text-secondary);
    line-height: 1.6;
}

.enterprise-panel__cta {
    margin-top: 22px;
}

.enterprise-inline-note {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 18px;
    padding: 14px 16px;
    border: 1px solid var(--enterprise-primary-border);
    border-radius: 16px;
    background: var(--enterprise-primary-soft-alt);
    color: var(--enterprise-primary);
}

.enterprise-summary-list {
    display: grid;
    gap: 14px;
    margin: 0;
}

.enterprise-summary-list div {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--enterprise-border-soft);
}

.enterprise-summary-list dt {
    color: var(--enterprise-text-secondary);
    font-weight: 700;
}

.enterprise-summary-list dd {
    margin: 0;
    color: var(--enterprise-text);
    text-align: right;
    font-weight: 600;
}

.enterprise-invoice-note {
    display: flex;
    gap: 14px;
    margin-top: 22px;
    padding: 18px;
    border: 1px solid var(--enterprise-warning-border);
    border-radius: 18px;
    background: var(--enterprise-warning-soft);
}

.enterprise-invoice-note__icon {
    color: #b96502;
}

.enterprise-invoice-note strong {
    display: block;
    margin-bottom: 6px;
    color: var(--enterprise-text);
}

.enterprise-invoice-note p {
    margin: 0;
    color: var(--enterprise-text-secondary);
}

.enterprise-steps {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 18px;
}

.enterprise-step {
    position: relative;
    padding-top: 8px;
}

.enterprise-step__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-bottom: 14px;
    border-radius: 50%;
    background: var(--enterprise-primary);
    color: var(--sd-text-inverse);
    font-weight: 800;
}

.enterprise-step__icon {
    margin-bottom: 14px;
}

.enterprise-card--assist {
    background:
        linear-gradient(
            145deg,
            rgba(31, 184, 108, 0.08),
            rgba(255, 255, 255, 0)
        ),
        var(--enterprise-surface) !important;
}

.enterprise-assist__cta {
    margin: 18px 0 12px;
    border-color: var(--enterprise-success-border) !important;
    background: var(--enterprise-success-soft) !important;
}

.enterprise-assist__email {
    margin: 0;
    color: var(--enterprise-text-secondary);
}

.enterprise-assist__email a {
    color: #149451;
    font-weight: 700;
    text-decoration: none;
}

.enterprise-assist__email a:hover {
    text-decoration: underline;
}

.enterprise-card--security {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 22px;
}

.enterprise-security__intro {
    display: flex;
    align-items: flex-start;
    gap: 14px;
}

.enterprise-security__intro h2 {
    margin: 0 0 6px;
    font-size: 1.3rem;
}

.enterprise-security__items {
    display: grid;
    grid-template-columns: repeat(4, max-content);
    gap: 22px;
}

.enterprise-security__items div {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--enterprise-text-secondary);
    font-weight: 600;
}

@media (max-width: 1180px) {
    .enterprise-contact-hero,
    .enterprise-contact-main,
    .enterprise-contact-secondary,
    .enterprise-steps,
    .enterprise-security__items {
        grid-template-columns: 1fr;
    }

    .enterprise-card-grid {
        grid-template-columns: 1fr;
    }

    .enterprise-card--security {
        align-items: flex-start;
        flex-direction: column;
    }
}

@media (max-width: 700px) {
    .enterprise-card--contact,
    .enterprise-card--summary,
    .enterprise-card--next,
    .enterprise-card--assist,
    .enterprise-card--security {
        padding: 20px;
    }

    .enterprise-contact-copy h1 {
        font-size: 2.1rem;
    }

    .enterprise-contact-illustration {
        min-height: 220px;
    }

    .enterprise-summary-list div {
        flex-direction: column;
    }

    .enterprise-summary-list dd {
        text-align: left;
    }
}
</style>
